import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Search, Download, ArrowRight, Sparkles } from 'lucide-react';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import QuickInstallModal from '../components/dashboard/QuickInstallModal';
import ThemeToggle from '@/components/ThemeToggle';

const categories = ['All', 'Payments', 'Authentication', 'Design Systems', 'Databases', 'Email/SMS'];

const allPlugins = [
    {
        id: 1,
        name: 'Razorpay MCP',
        description: 'Accept payments with India\'s #1 payment gateway. UPI, cards, wallets, and 100+ payment modes.',
        logo: 'https://logo.clearbit.com/razorpay.com',
        url: 'pluckr://verified/razorpay-mcp@v2.1.4',
        uses: '12.4k',
        updated: '2 days ago',
        category: 'Payments',
        featured: true
    },
    {
        id: 2,
        name: 'Stripe India MCP',
        description: 'International payments with local Indian support. Cards, wallets, and bank transfers.',
        logo: 'https://logo.clearbit.com/stripe.com',
        url: 'pluckr://verified/stripe-india@v3.1.2',
        uses: '18.9k',
        updated: '1 day ago',
        category: 'Payments'
    },
    {
        id: 3,
        name: 'Paytm Business MCP',
        description: 'UPI and wallet payments with Paytm for Business integration.',
        logo: 'https://logo.clearbit.com/paytm.com',
        url: 'pluckr://verified/paytm-mcp@v1.8.2',
        uses: '8.2k',
        updated: '5 days ago',
        category: 'Payments'
    },
    {
        id: 4,
        name: 'PhonePe MCP',
        description: 'Payment and merchant APIs for PhonePe integration.',
        logo: 'https://logo.clearbit.com/phonepe.com',
        url: 'pluckr://verified/phonepe-mcp@v1.5.0',
        uses: '6.7k',
        updated: '1 week ago',
        category: 'Payments'
    },
    {
        id: 5,
        name: 'Supabase Auth MCP',
        description: 'Complete authentication flows with Supabase backend. Social logins, magic links, and more.',
        logo: 'https://logo.clearbit.com/supabase.com',
        url: 'pluckr://verified/supabase-auth@v3.2.1',
        uses: '15.8k',
        updated: '3 days ago',
        category: 'Authentication'
    },
    {
        id: 6,
        name: 'Clerk Auth MCP',
        description: 'User management with beautiful pre-built components. SSO, MFA, and user profiles.',
        logo: 'https://logo.clearbit.com/clerk.com',
        url: 'pluckr://verified/clerk-auth@v2.4.0',
        uses: '14.5k',
        updated: '2 days ago',
        category: 'Authentication'
    },
    {
        id: 7,
        name: 'Shadcn UI System',
        description: 'Design system components built on Radix primitives. Fully customizable and accessible.',
        logo: 'https://ui.shadcn.com/apple-touch-icon.png',
        url: 'pluckr://verified/shadcn-ui@v4.0.0',
        uses: '22.1k',
        updated: '1 day ago',
        category: 'Design Systems'
    },
    {
        id: 8,
        name: 'Resend Email MCP',
        description: 'Transactional emails with beautiful templates. React Email compatible.',
        logo: 'https://logo.clearbit.com/resend.com',
        url: 'pluckr://verified/resend-mcp@v2.0.3',
        uses: '9.3k',
        updated: '4 days ago',
        category: 'Email/SMS'
    },
    {
        id: 9,
        name: 'Twilio SMS MCP',
        description: 'SMS notifications and verification flows. Global reach with local numbers.',
        logo: 'https://logo.clearbit.com/twilio.com',
        url: 'pluckr://verified/twilio-sms@v1.9.1',
        uses: '11.2k',
        updated: '6 days ago',
        category: 'Email/SMS'
    },
    {
        id: 10,
        name: 'MongoDB Atlas MCP',
        description: 'Cloud database with automatic scaling. Full-text search and aggregation pipelines.',
        logo: 'https://logo.clearbit.com/mongodb.com',
        url: 'pluckr://verified/mongodb-atlas@v2.3.0',
        uses: '13.7k',
        updated: '3 days ago',
        category: 'Databases'
    },
    {
        id: 11,
        name: 'Firebase MCP',
        description: 'Real-time database, authentication, and cloud functions in one package.',
        logo: 'https://logo.clearbit.com/firebase.google.com',
        url: 'pluckr://verified/firebase-mcp@v3.0.1',
        uses: '16.2k',
        updated: '2 days ago',
        category: 'Databases'
    },
];

export default function Plugins() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
    const [visibleCards, setVisibleCards] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Check if user is logged in on mount
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        setIsLoggedIn(!!token);
    }, []);

    // Memoize filtered plugins to avoid dependency issues
    const filteredPlugins = useMemo(() => {
        return allPlugins
            .filter(p => activeCategory === 'All' || p.category === activeCategory)
            .filter(p =>
                searchQuery === '' ||
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
    }, [activeCategory, searchQuery]);

    // Animate cards when filtered plugins change
    useEffect(() => {
        setVisibleCards([]);
        const timer = setTimeout(() => {
            setVisibleCards(filteredPlugins.map(p => p.id));
        }, 100);
        return () => clearTimeout(timer);
    }, [filteredPlugins]);

    const handleCopy = (id, url) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#f8f9fa]/80 dark:bg-[#161616]/80 backdrop-blur-xl border-b border-[#e0e0e0] dark:border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-20 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src={pluckrLogo} alt="Pluckr" className="h-8 w-auto" />
                        <span className="text-xl font-bold text-[#1a1a1a] dark:text-white" style={{ fontFamily: "'Lora', serif" }}>Pluckr</span>
                    </Link>
                    <div className="hidden md:flex items-center gap-8">
                        <Link to="/#aimcpservers" className="text-[#9be38f] font-medium">AI MCP Servers</Link>
                        <Link to="/#how-it-works" className="text-[#5a5a5a] dark:text-[#e0e0e0] hover:text-[#1a1a1a] dark:hover:text-white transition-colors">How It Works</Link>
                        <Link to="/#features" className="text-[#5a5a5a] dark:text-[#e0e0e0] hover:text-[#1a1a1a] dark:hover:text-white transition-colors">Features</Link>
                    </div>
                    {isLoggedIn ? (
                        <Link
                            to="/profile"
                            className="px-6 py-2 rounded-full bg-[#9be38f] text-[#1a1a1a] font-semibold hover:bg-[#8ad37f] transition-colors"
                        >
                            Profile
                        </Link>
                    ) : (
                        <Link
                            to="/early-access"
                            className="px-6 py-2 rounded-full bg-[#9be38f] text-[#1a1a1a] font-semibold hover:bg-[#8ad37f] transition-colors"
                        >
                            Get Early Access
                        </Link>
                    )}
                </div>

                {/* Theme toggle: placed in header area without affecting layout flow */}
                <ThemeToggle className="fixed top-2.5 right-[4.75rem] md:right-[14.5rem] z-[60]" />
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-16 px-6 lg:px-20 relative overflow-hidden">
                {/* Background gradient */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(155, 227, 143, 0.15), transparent)'
                    }}
                />

                <div className="max-w-[1400px] mx-auto text-center relative">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#9be38f]/20 text-[#2d8a2d] text-sm font-medium mb-6">
                        <Sparkles className="w-4 h-4" />
                        {allPlugins.length} Verified Plugins Available
                    </div>

                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] dark:text-white mb-6"
                        style={{ fontFamily: "'Lora', serif" }}
                    >
                        Plugin Marketplace
                    </h1>
                    <p className="text-lg sm:text-xl text-[#5a5a5a] dark:text-[#e0e0e0] max-w-[700px] mx-auto mb-10">
                        Pre-built, verified MCP modules for payments, auth, design systems, and more.
                        Every plugin is sandboxed, deterministic, and production-ready.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5a5a5a] dark:text-white/60" />
                        <input
                            type="text"
                            placeholder="Search plugins..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-[#e0e0e0] dark:border-white/20 bg-white dark:bg-[#282929] text-[#1a1a1a] dark:text-white placeholder-[#999] dark:placeholder-white/50 focus:border-[#9be38f] focus:outline-none transition-colors text-lg"
                        />
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="pb-24 px-6 lg:px-20">
                <div className="max-w-[1400px] mx-auto">
                    {/* Category Filters */}
                    <div className="flex flex-wrap gap-3 justify-center mb-12">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => {
                                    setActiveCategory(category);
                                    setVisibleCards([]);
                                }}
                                className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${activeCategory === category
                                    ? 'bg-[#1a1a1a] text-white shadow-lg'
                                    : 'bg-white dark:bg-[#282929] border-2 border-[#e0e0e0] dark:border-white/15 text-[#5a5a5a] dark:text-[#e0e0e0] hover:border-[#9be38f] hover:text-[#1a1a1a] dark:hover:text-white'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Plugin Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredPlugins.map((plugin, index) => (
                            <div
                                key={plugin.id}
                                className={`plugin-card bg-white dark:bg-[#282929] rounded-2xl border-2 border-[#e8e8e8] dark:border-white/15 p-6 transition-all duration-500 hover:border-[#9be38f] hover:shadow-xl hover:shadow-[#9be38f]/10 hover:-translate-y-1 ${visibleCards.includes(plugin.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                                    }`}
                                style={{
                                    transitionDelay: `${index * 50}ms`,
                                }}
                            >
                                {/* Featured Badge */}
                                {plugin.featured && (
                                    <div className="absolute -top-3 -right-3 px-3 py-1 rounded-full bg-gradient-to-r from-[#9be38f] to-[#6dd55f] text-[#1a1a1a] text-xs font-bold shadow-lg">
                                        ⭐ Featured
                                    </div>
                                )}

                                {/* Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-14 h-14 rounded-xl bg-[#f5f5f5] dark:bg-white/5 border border-[#e0e0e0] dark:border-white/15 flex items-center justify-center p-2 overflow-hidden">
                                        <img
                                            src={plugin.logo}
                                            alt={plugin.name}
                                            className="w-10 h-10 object-contain"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#e8f5e3] dark:bg-[#9be38f]/15 text-[#2d8a2d] dark:text-[#9be38f] text-xs font-medium">
                                        <Check className="w-3.5 h-3.5" />
                                        Verified
                                    </div>
                                </div>

                                {/* Content */}
                                <h3 className="text-lg font-semibold text-[#1a1a1a] dark:text-white mb-2">{plugin.name}</h3>
                                <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mb-5 line-clamp-2 min-h-[40px]">{plugin.description}</p>

                                {/* URL + Copy */}
                                <div className="flex items-center gap-2 mb-5">
                                    <div className="flex-1 bg-[#1a1a1a] rounded-lg px-3 py-2 overflow-hidden">
                                        <code className="text-[#00d4ff] text-xs font-mono truncate block">
                                            {plugin.url}
                                        </code>
                                    </div>
                                    <button
                                        onClick={() => handleCopy(plugin.id, plugin.url)}
                                        className={`p-2.5 rounded-lg transition-all ${copiedId === plugin.id
                                            ? 'bg-[#9be38f] text-[#1a1a1a]'
                                            : 'bg-[#f0f0f0] dark:bg-white/10 text-[#5a5a5a] dark:text-white/70 hover:bg-[#e0e0e0] dark:hover:bg-white/15'
                                            }`}
                                        title="Copy URL"
                                    >
                                        {copiedId === plugin.id ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>

                                {/* Quick Install Button */}
                                <button
                                    onClick={() => setIsInstallModalOpen(true)}
                                    className="w-full py-3 rounded-xl font-semibold text-sm transition-all bg-gradient-to-r from-[#9be38f] to-[#7dd87d] text-[#1a1a1a] hover:from-[#8ad37f] hover:to-[#6dc86d] flex items-center justify-center gap-2 group"
                                >
                                    <Download className="w-4 h-4" />
                                    Quick Install
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>

                                {/* Footer Stats */}
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#f0f0f0] dark:border-white/10 text-xs text-[#888] dark:text-white/60">
                                    <span>{plugin.uses} installs</span>
                                    <span>Updated {plugin.updated}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Empty State */}
                    {filteredPlugins.length === 0 && (
                        <div className="text-center py-16">
                            <div className="w-20 h-20 rounded-full bg-[#f5f5f5] dark:bg-white/5 flex items-center justify-center mx-auto mb-6">
                                <Search className="w-8 h-8 text-[#ccc] dark:text-white/30" />
                            </div>
                            <h3 className="text-xl font-semibold text-[#1a1a1a] dark:text-white mb-2">No plugins found</h3>
                            <p className="text-[#5a5a5a] dark:text-[#e0e0e0]">Try adjusting your search or filter criteria</p>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 lg:px-20 bg-[#1a1a1a]">
                <div className="max-w-[800px] mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', serif" }}>
                        Can't find what you're looking for?
                    </h2>
                    <p className="text-[#888] text-lg mb-8">
                        Request a plugin or build your own. We're expanding the verified catalog every week.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/early-access"
                            className="px-8 py-4 rounded-full bg-gradient-to-r from-[#9be38f] to-[#7dd87d] text-[#1a1a1a] font-semibold hover:from-[#8ad37f] hover:to-[#6dc86d] transition-all"
                        >
                            Request a Plugin
                        </Link>
                        <a
                            href="https://docs.pluckr.ai/plugins/create"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 rounded-full border-2 border-[#333] text-white font-semibold hover:border-[#9be38f] hover:text-[#9be38f] transition-all"
                        >
                            Build Your Own
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6 lg:px-20 border-t border-[#e0e0e0] dark:border-white/10">
                <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <img src={pluckrLogo} alt="Pluckr" className="h-6 w-auto" />
                        <span className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm">© 2026 Pluckr. All rights reserved.</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-[#5a5a5a] dark:text-[#e0e0e0]">
                        <a href="https://docs.pluckr.ai" className="hover:text-[#1a1a1a] dark:hover:text-white transition-colors">Docs</a>
                        <a href="https://github.com/pluckr" className="hover:text-[#1a1a1a] dark:hover:text-white transition-colors">GitHub</a>
                        <a href="mailto:support@pluckr.ai" className="hover:text-[#1a1a1a] dark:hover:text-white transition-colors">Support</a>
                    </div>
                </div>
            </footer>

            {/* Quick Install Modal */}
            <QuickInstallModal
                isOpen={isInstallModalOpen}
                onClose={() => setIsInstallModalOpen(false)}
            />
        </div>
    );
}
